import { isMobile } from '@/api/utils'

// iPadOS 13+（含 App 內 WKWebView）預設回報桌機 UA（Macintosh），UA 正則認不出來，
// 用 maxTouchPoints 補判——真正的 Mac 不會有多點觸控
const isIpadWithDesktopUA = () => /Mac/.test(navigator.userAgent) && navigator.maxTouchPoints > 1

let cachedPanoramaWidthLimit = null

/**
 * 該裝置可安全載入的全景圖寬度上限（VM-906）：
 * min(WebGL MAX_TEXTURE_SIZE, 行動裝置 4096 / 桌機 8192)。
 * 16K 原圖在 iOS WKWebView 解碼即超出 Web Content Process 記憶體上限（約 1~1.5GB）被 Jetsam 終止，
 * 必須在「決定要下載哪張圖」的層級就限制，不能等 krpano/GPU 端再降階。
 *
 * @returns {number} panorama sphere 圖的寬度上限
 */
export const getPanoramaWidthLimit = () => {
  if (cachedPanoramaWidthLimit) return cachedPanoramaWidthLimit
  let maxTextureSize = 8192
  try {
    const canvas = document.createElement('canvas')
    const gl = canvas.getContext('webgl') || canvas.getContext('experimental-webgl')
    if (gl) {
      maxTextureSize = gl.getParameter(gl.MAX_TEXTURE_SIZE) || maxTextureSize
      const loseContext = gl.getExtension('WEBGL_lose_context')
      if (loseContext) loseContext.loseContext()
    }
  } catch (e) {
    // 查不到就用保守預設 8192
  }
  const deviceLimit = isMobile() || isIpadWithDesktopUA() ? 4096 : 8192
  cachedPanoramaWidthLimit = Math.min(maxTextureSize, deviceLimit)
  return cachedPanoramaWidthLimit
}

/**
 * Resize any images with Cloudflare workers
 *
 * @param {string} url - source url
 * @param {number} width - Specifies maximum width of the image in pixels.
 * @param {number} quality - Specifies quality for images in JPEG, WebP, and AVIF formats. The quality is in a 1-100 scale, but useful values are between 50 (low quality, small file size) and 90 (high quality, large file size). 85 is the default.
 * @param {string} fit - Affects interpretation of width and height. All resizing modes preserve aspect ratio.
 * @returns {string} resized url
 */
export const resizeImage = (url, width = 512, quality = 100, fit = 'scale-down') => {
  if (!url) {
    return ''
  }
  const instance = new URL(url)
  if (!/cdn(-dev)?\.istaging\.com/.test(url)) {
    instance.protocol = 'https:'
    instance.host = 'resize-anywhere.istaging.com'
    instance.pathname = ''
    instance.searchParams.set('image', url)
  }
  instance.searchParams.set('w', width)
  instance.searchParams.set('q', quality)
  instance.searchParams.set('fit', fit)
  return instance.href
}

/**
 * Resize images host on iStaging CDN with Cloudflare workers
 *
 * @param {string} url - source url
 * @param {number} width - Specifies maximum width of the image in pixels.
 * @param {number} quality - Specifies quality for images in JPEG, WebP, and AVIF formats. The quality is in a 1-100 scale, but useful values are between 50 (low quality, small file size) and 90 (high quality, large file size). 85 is the default.
 * @param {string} fit - Affects interpretation of width and height. All resizing modes preserve aspect ratio.
 * @returns {string} resized url
 */
export const resizeCDNImage = (url = '', width, quality = 80, fit = 'scale-down') => {
  if (!url) {
    return ''
  }
  const instance = new URL(url)
  instance.searchParams.set('w', width)
  instance.searchParams.set('q', quality)
  instance.searchParams.set('fit', fit)
  return instance.href
}
